import ProjectWorld, { createServerEvent, ProjectEventType, type ProjectUser } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { AccessLevel, ServerEvent } from "@utilitydelta/eventplanedb-client";

export const MINOR_VERSION: number = 1;

export interface ColorSquareCreated_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.ColorSquareCreated;
    gridSize: number; // Size of the grid (both width and height)
}

export interface ColorSquareCreated_ProjectionResult extends BaseProjectionResult {
    kind: 'ColorSquareCreated_ProjectionResult';
    gridSize: number; // Size of the grid
    createdByClientId: string; // Client who created the project
    createdByUserId?: string; // User who created the project
    createdOn: number; // Timestamp of creation
    projectUser: ProjectUser; // User who created the project
}

export interface ColorSquareCreated_EventInputs extends BaseEventInput {
    kind: 'ColorSquareCreated_EventInputs';
    gridSize: number; // Size of the grid (both width and height)
}

export type ColorSquareCreated_EventProcessorType = EventProcessor<ProjectEventType, ColorSquareCreated_EventInputs, ColorSquareCreated_LocalEvent, ColorSquareCreated_ProjectionResult, ColorSquareCreated_LocalEvent, ServerEvent<ProjectEventType>>;

export class ColorSquareCreated_EventProcessor implements ColorSquareCreated_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.ColorSquareCreated; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.ColorSquareCreated; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: ColorSquareCreated_LocalEvent, _originalEventProjectionResult: ColorSquareCreated_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: ColorSquareCreated_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): ColorSquareCreated_ProjectionResult | undefined => {
        // Update metadata with creation information
        this.projectWorld.projectMetadata.createdOn = event.et;
        this.projectWorld.projectMetadata.modifiedOn = event.et;

        this.projectWorld.projectMetadata.createdByClientId = event.ci;
        this.projectWorld.projectMetadata.modifiedByClientId = event.ci;

        this.projectWorld.projectMetadata.createdByUserId = event.ui;
        this.projectWorld.projectMetadata.modifiedByUserId = event.ui;

        this.projectWorld.projectMetadata.gridSize = event.gridSize;

        const id = event.ui ?? event.ci;
        if (!id) {
            return undefined;
        }

        let projectUser = this.projectWorld.projectUsers.maybeGetIgnoreDeleted(id);
        if (!projectUser) {
            projectUser = {
                id,
                isOAuthUser: event.ui !== undefined,
                isDeleted: false,
                pi: this.projectWorld.projectMetadata.id,
                lastEventTime: { [event.tp]: event.et } as Record<ProjectEventType, number>,
                accessUpdatedOn: event.et,
                createdOn: event.et,
                createdByClientId: event.ci,
                createdByUserId: event.ui,
                modifiedOn: event.et,
                modifiedByClientId: event.ci,
                modifiedByUserId: event.ui,
                lastSeenOn: event.et,
                accessLevel: AccessLevel.Owner,
                color: 0,
                randomUsername: undefined, // Random username can be set later
            }
            this.projectWorld.projectUsers.upsert(projectUser);
        }

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('projectUsers', projectUser);
        }

        return !returnProjectionResults ? undefined : {
            kind: 'ColorSquareCreated_ProjectionResult',
            gridSize: event.gridSize,
            createdByClientId: event.ci,
            createdByUserId: event.ui,
            createdOn: event.et,
            projectUser
        };
    }

    public createLocalEvent = (eventToPopulate: ColorSquareCreated_LocalEvent, userInputs: ColorSquareCreated_EventInputs): boolean => {
        if (eventToPopulate.gridSize <= 0) return false;

        eventToPopulate.gridSize = userInputs.gridSize;
        return true;
    }

    public toServerEvent = async (localEvent: ColorSquareCreated_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            gridSize: localEvent.gridSize
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: ColorSquareCreated_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}